import { Fragment } from "react";
import SectionHead from "@/components/common/SectionHead";
import Reveal from "@/components/common/Reveal";
import { Check } from "@/components/common/icons";
import { COMPARE_ROWS } from "@/lib/data";
import { useLocale } from "@/i18n/useLocale";
import { landingCompareFor } from "@/content/landing";
import { cx } from "@/lib/cx";

export default function Comparison(): JSX.Element {
  const t = landingCompareFor(useLocale());

  return (
    <section className="section-y-lg bg-bg-2 border-y border-border">
      <div className="container-page">
        <SectionHead
          title={
            <>
              {t.title.lead}
              <br />
              <span className="text-brand-gradient">{t.title.highlight}</span>
              {t.title.tail}
            </>
          }
          sub={t.sub}
        />

        <Reveal>
          <div className="md:hidden mt-8 glass-card overflow-hidden">
            <div className="grid grid-cols-2">
              <div
                className="px-3 py-3.5 border-b border-border relative overflow-hidden"
                style={{
                  background:
                    "linear-gradient(180deg, rgb(255 255 255 / 0.22) 0%, rgb(255 255 255 / 0.09) 45%, rgb(255 255 255 / 0.04) 100%)",
                  boxShadow:
                    "inset 0 1px 0 rgb(255 255 255 / 0.32), inset 0 -1px 0 rgb(0 0 0 / 0.5)",
                }}
              >
                <div className="font-mono text-[11px] font-semibold text-fg/90 tracking-[0.16em]">{t.headers.otherFirms}</div>
                <div className="font-display text-[15px] mt-1 text-fg leading-tight">{t.headers.otherFirmsSub}</div>
              </div>
              <div
                className="px-3 py-3.5 border-b border-l border-border relative overflow-hidden"
                style={{
                  background:
                    "linear-gradient(180deg, rgb(var(--brand) / 0.7) 0%, rgb(var(--brand) / 0.35) 55%, rgb(var(--brand) / 0.15) 100%)",
                  boxShadow:
                    "inset 0 1px 0 rgb(255 255 255 / 0.35), inset 0 -22px 30px -18px rgb(var(--brand) / 0.85), 0 0 18px -2px rgb(var(--brand) / 0.55)",
                }}
              >
                <div className="font-mono text-[11px] font-semibold text-white tracking-[0.16em]">{t.headers.ours}</div>
                <div className="font-display text-[15px] mt-1 text-white leading-tight drop-shadow-[0_1px_2px_rgb(0_0_0_/_0.4)]">{t.headers.oursSub}</div>
              </div>

              {COMPARE_ROWS.map((row, i) => {
                const last = i === COMPARE_ROWS.length - 1;
                return (
                  <Fragment key={row.key}>
                    <div className="col-span-2 flex gap-2 items-center px-3 py-2 border-b border-border bg-bg-2/40">
                      <span className="font-mono text-[10px] text-fg-dim tracking-[0.14em] shrink-0">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span aria-hidden="true" className="font-mono text-brand-emphasis text-[13px] shrink-0">
                        {row.glyph}
                      </span>
                      <span className="text-[13px] font-semibold leading-tight">{t.rows[row.key].topic}</span>
                    </div>
                    <div
                      className={cx(
                        "compare-other flex gap-2 items-start px-3 py-2.5",
                        !last && "border-b border-border"
                      )}
                    >
                      <span aria-hidden="true" className="shrink-0 text-fg-dim text-sm leading-none mt-px">
                        ×
                      </span>
                      <span className="text-[12px] leading-[1.4] text-fg-muted">{t.rows[row.key].legacy}</span>
                    </div>
                    <div
                      className={cx(
                        "compare-ours flex gap-2 items-start px-3 py-2.5 border-l border-border",
                        !last && "border-b"
                      )}
                    >
                      <span className="shrink-0 text-brand-emphasis mt-px">
                        <Check size={12} />
                      </span>
                      <span className="text-[12px] leading-[1.4] text-fg">{t.rows[row.key].ours}</span>
                    </div>
                  </Fragment>
                );
              })}
            </div>
          </div>

          <div className="hidden md:block glass-card mt-8 overflow-hidden">
            <div className="compare-table">
              {/* Column headers row */}
              <div className="compare-cell" style={{ background: "rgb(var(--bg-2) / 0.4)" }} />
              <div className="compare-cell compare-other left-bord">
                <div className="font-mono text-[10.5px] text-fg-dim tracking-[0.14em]">{t.headers.otherFirms}</div>
                <div className="font-display text-base md:text-lg mt-0.5 text-fg-muted">{t.headers.otherFirmsSub}</div>
              </div>
              <div className="compare-cell compare-ours left-bord">
                <div className="font-mono text-[10.5px] text-brand-emphasis tracking-[0.14em]">{t.headers.ours}</div>
                <div className="font-display text-base md:text-lg mt-0.5 text-brand-emphasis">{t.headers.oursSub}</div>
              </div>

              {/* Row data */}
              {COMPARE_ROWS.map((row, i) => {
                const last = i === COMPARE_ROWS.length - 1;
                const cellCls = cx("compare-cell", last && "last");
                return (
                  <Fragment key={row.key}>
                    <div className={cx(cellCls, "flex gap-3 items-start")}>
                      <span className="font-mono text-[10.5px] text-fg-dim tracking-[0.14em] w-[18px] shrink-0 pt-0.5">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span
                        aria-hidden="true"
                        className="font-mono text-brand-emphasis text-sm w-[18px] shrink-0 text-center pt-px"
                      >
                        {row.glyph}
                      </span>
                      <span className="text-sm md:text-[15px] font-semibold">{t.rows[row.key].topic}</span>
                    </div>
                    <div className={cx(cellCls, "compare-other left-bord flex gap-2.5 items-start")}>
                      <span aria-hidden="true" className="shrink-0 text-fg-dim text-base leading-none mt-0.5">
                        ×
                      </span>
                      <span className="text-[13px] md:text-sm">{t.rows[row.key].legacy}</span>
                    </div>
                    <div className={cx(cellCls, "compare-ours left-bord flex gap-2.5 items-start")}>
                      <span className="shrink-0 text-brand-emphasis mt-0.5">
                        <Check size={14} />
                      </span>
                      <span className="text-[13px] md:text-sm text-fg">{t.rows[row.key].ours}</span>
                    </div>
                  </Fragment>
                );
              })}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
